// ===========================================
// ImageField.jsx
// Componente universal de imagen (bytea) — MAPASAC ERP v2026
// Usado por SqlexecBuilder para field.type === 'image'
// ===========================================

import React, { useRef } from 'react';
import { Image as ImageIcon, Upload, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';

// ===========================================
// ImageField
// Props:
//   field         — { name, label, required, gridSpan, maxSizeMb }
//   previewBase64 — base64 actual a mostrar (imagen existente o recién elegida)
//   previewMime   — mime type de previewBase64 (ej. 'image/png')
//   onChange      — (base64OrNull, mime) => void
//                     base64OrNull = null  → usuario presionó "Quitar imagen"
//                     base64OrNull = string → nuevo archivo elegido
//   disabled      — readonly (modo view)
//   showError / errorMessage
//   labelStyles / inputStyles / errorStyles — estilos desde sisconfig (formulario_ui)
//   btnBase / btnPrimary / btnSecondary — estilos de botones desde sisconfig (botones_ui);
//   btnBase.danger se usa internamente para el botón "Quitar imagen"
// ===========================================
const ImageField = ({
  field,
  previewBase64,
  previewMime = 'image/png',
  onChange,
  disabled = false,
  showError = false,
  errorMessage = null,
  labelStyles = {},
  inputStyles = {},
  errorStyles = {},
  btnBase = {},
  btnPrimary: _btnPrimary = {},
  btnSecondary = {}
}) => {
  const fileInputRef = useRef(null);
  const btnDanger = btnBase.danger || {};
  const maxSizeMb = field?.maxSizeMb || 2;
  const accept = field?.accept || 'image/*';
  const acceptList = accept.split(',').map(s => s.trim()).filter(Boolean);

  const isTipoPermitido = (mimeType) => {
    if (acceptList.includes('image/*')) return mimeType.startsWith('image/');
    return acceptList.includes(mimeType);
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-seleccionar el mismo archivo

    if (!file) return;

    if (!isTipoPermitido(file.type)) {
      toast.error(`Tipo de archivo no permitido: ${file.type || 'desconocido'} (permitidos: ${accept})`);
      return;
    }
    if (file.size / (1024 * 1024) > maxSizeMb) {
      toast.error(`Imagen demasiado grande (máximo ${maxSizeMb} MB)`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      onChange?.(base64, file.type);
    };
    reader.onerror = () => toast.error('No se pudo leer el archivo');
    reader.readAsDataURL(file);
  };

  const handleQuitarImagen = () => {
    onChange?.(null, 'image/png');
  };

  return (
    <div>
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: labelStyles.marginBottom || '0.5rem' }}
      >
        <label
          style={{
            fontSize: labelStyles.fontSize || '0.875rem',
            fontWeight: labelStyles.fontWeight || '500',
            color: labelStyles.textColor || '#374151'
          }}
        >
          {field?.label}{!disabled && field?.required && <span className="text-red-500 ml-1">*</span>}
        </label>

        {!disabled && (
          <div className="flex items-center gap-2">
            {!previewBase64 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                title="Seleccionar imagen"
                className="flex items-center gap-1"
                style={{
                  fontSize: btnBase.fontSize || '0.75rem',
                  fontFamily: btnBase.fontFamily || 'inherit',
                  paddingLeft: btnBase.padding?.x || '1rem',
                  paddingRight: btnBase.padding?.x || '1rem',
                  paddingTop: btnBase.padding?.y || '0.5rem',
                  paddingBottom: btnBase.padding?.y || '0.5rem',
                  borderRadius: btnBase.borderRadius || '6px',
                  borderWidth: btnBase.borderWidth || '1px',
                  borderStyle: 'solid',
                  borderColor: btnSecondary.borderColor || '#d1d5db',
                  backgroundColor: btnSecondary.backgroundColor || '#ffffff',
                  color: btnSecondary.textColor || '#374151',
                  cursor: 'pointer'
                }}
              >
                <Upload size={12} />
                Seleccionar imagen
              </button>
            )}

            {previewBase64 && (
              <button
                type="button"
                onClick={handleQuitarImagen}
                title="Quitar imagen"
                className="flex items-center gap-1"
                style={{
                  fontSize: btnBase.fontSize || '0.75rem',
                  fontFamily: btnBase.fontFamily || 'inherit',
                  paddingLeft: btnBase.padding?.x || '1rem',
                  paddingRight: btnBase.padding?.x || '1rem',
                  paddingTop: btnBase.padding?.y || '0.5rem',
                  paddingBottom: btnBase.padding?.y || '0.5rem',
                  borderRadius: btnBase.borderRadius || '6px',
                  borderWidth: btnBase.borderWidth || '1px',
                  borderStyle: 'solid',
                  borderColor: btnDanger.borderColor || '#dc2626',
                  backgroundColor: btnDanger.backgroundColor || '#dc2626',
                  color: btnDanger.textColor || '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <Trash2 size={12} />
                Quitar imagen
              </button>
            )}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />

      <div
        style={{
          borderWidth: inputStyles.borderWidth || '1px',
          borderStyle: 'solid',
          borderColor: inputStyles.borderColor || '#d1d5db',
          borderRadius: inputStyles.borderRadius || '6px',
          backgroundColor: inputStyles.backgroundColor || '#ffffff',
          padding: inputStyles.padding?.x || '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '120px'
        }}
      >
        {previewBase64 ? (
          <img
            src={`data:${previewMime};base64,${previewBase64}`}
            alt={field?.label}
            style={{ maxWidth: '100%', maxHeight: '160px', objectFit: 'contain' }}
          />
        ) : (
          <div className="flex flex-col items-center gap-1" style={{ color: inputStyles.placeholderColor || '#9ca3af' }}>
            <ImageIcon size={24} />
            <span style={{ fontSize: inputStyles.fontSize || '0.75rem', fontStyle: 'italic' }}>Sin imagen</span>
          </div>
        )}
      </div>

      {showError && (
        <p
          style={{
            fontSize: errorStyles.fontSize || '0.75rem',
            color: errorStyles.textColor || '#dc2626',
            marginTop: errorStyles.marginTop || '0.25rem'
          }}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default ImageField;
